import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  Flex,
  Image,
  Center,
  Field,
} from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
  SelectInput,
  TextareaField,
  ToggleButton,
  ToggleGroup,
  type ToggleOption,
} from '@themes/aegix';

// The binary on/off controls in this drawer (Approval Required, Default
// Alert, Reverse Alert, Default Incident, System protocol, Prompt for
// Location) use the themed `<ToggleButton>` from `@themes/aegix` — the
// same primitive SiteDrawer / UsersForm / SitesForm / NotificationSection
// use. One platform pattern, no local re-implementation.
//
// Likewise the urgency / activation-mode / reverse-unit pickers below
// render the themed `<ToggleGroup>` (`@themes/aegix/layout`) directly.
import {
  useCreateAlertType,
  useUpdateAlertType,
  useUploadAlertTypeFile,
  useAlertGuidances,
  useCreateAlertGuidance,
  useUpdateAlertGuidance,
  useDeleteAlertGuidance,
  useReportSteps,
  useBulkCreateReportSteps,
  useBulkUpdateReportSteps,
  useDeleteReportStep,
  useSites,
} from '../../../hooks/queries';
import type {
  AlertSound,
  AlertType,
  DrillSound,
  ReportStep,
  ReportStepPhase,
} from '../../../services';
import { ALERT_SOUND_DEFAULT, DRILL_SOUND_DEFAULT } from '../../../services';
import { useScopedFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import { toaster } from '../../ui/toaster';
import {
  ProtocolGroupSelector,
  type GroupSelection,
} from './ProtocolGroupSelector';
import {
  ProtocolRecipientsEditor,
  type RubricScope,
} from './ProtocolRecipientsEditor';
import { ProtocolContactsEditor } from './ProtocolContactsEditor';
import {
  ProtocolGuidanceEditor,
  type GuidanceLocal,
} from './ProtocolGuidanceEditor';
import {
  ProtocolReportStepEditor,
  slugifyLabel,
  type ReportStepLocal,
} from './ProtocolReportStepEditor';
import { FaPlay, FaPause } from 'react-icons/fa';
import { MdNotificationsActive } from 'react-icons/md';

interface ProtocolDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  protocol: AlertType | null;
  /** Lifecycle actions live in the drawer footer rather than the list
   *  row's action column. The list still drives the actual
   *  mutation (it owns the React Query keys); the drawer just exposes
   *  the affordance. Both callbacks should close the drawer after
   *  firing. */
  onArchive?: (id: number) => void;
  onRestore?: (id: number) => void;
}

const urgencyOptions = ['emergency', 'attention', 'follow'] as const;
const modeOptions = ['alert', 'incident'] as const;

/** The drill-tone choices, in wire order.
 *
 *  `previewSrc` is null for `same_as_alert` — there is no fixed tone to
 *  audition, since what plays is whatever THIS protocol's alert sound is
 *  (the Alert Sound card above). The three drill tones are static assets
 *  served from `public/audio/`, byte-identical to the 27.75 s
 *  loudness-normalised masters shared with iOS and Android so all three
 *  clients audition the same audio. */
// Labels resolve at render. Module scope has no `t`, and labels are
// deliberately NOT stored here as dynamic keys:
// check-client-locale-catalogs.mjs only verifies STATIC t() calls (it skips
// dynamic ones), so the ternary in drillSoundLabel below is what actually
// gets CI coverage — same shape urgencyOptions uses.
const drillSoundOptions: {
  value: DrillSound;
  previewSrc: string | null;
}[] = [
    { value: 'same_as_alert', previewSrc: null },
    { value: 'drill_1', previewSrc: '/audio/drill-1.mp3' },
    { value: 'drill_2', previewSrc: '/audio/drill-2.mp3' },
    { value: 'drill_3', previewSrc: '/audio/drill-3.mp3' },
  ];

/** The real-alert tone choices, in wire order.
 *
 *  All three real TONES audition. Only the two sentinels have no
 *  `previewSrc`, and for one reason: they are not tones. What they resolve
 *  to is this protocol's own uploaded audio (or the client's bundled
 *  fallback), which the Media > Sound card above already auditions — there
 *  is no second copy to play here.
 *
 *  `alert-1.mp3` is the INCUMBENT tone, SHA-256 identical to Android's
 *  `res/raw/alert.mp3` (`782367553634…`). It is the file already shipping as
 *  `alert`, not a re-render, which is why it measures 27.746 s against the
 *  other five at exactly 27.750 s. Do not "normalise" that 4 ms away: the
 *  point of this asset is that it is bit-identical to what users already
 *  hear on every platform. */
const alertSoundOptions: {
  value: AlertSound;
  previewSrc: string | null;
}[] = [
    { value: 'default', previewSrc: null },
    { value: 'custom_upload', previewSrc: null },
    { value: 'alert_1', previewSrc: '/audio/alert-1.mp3' },
    { value: 'alert_2', previewSrc: '/audio/alert-2.mp3' },
    { value: 'alert_3', previewSrc: '/audio/alert-3.mp3' },
  ];

export function ProtocolDrawer({
  isOpen,
  onClose,
  protocol,
  onArchive,
  onRestore,
}: ProtocolDrawerProps) {
  const { t } = useTranslation();
  const isEditing = !!protocol;
  const createMutation = useCreateAlertType();
  const updateMutation = useUpdateAlertType();
  const uploadMutation = useUploadAlertTypeFile();
  const createGuidanceMutation = useCreateAlertGuidance();
  const updateGuidanceMutation = useUpdateAlertGuidance();
  const deleteGuidanceMutation = useDeleteAlertGuidance();
  const bulkCreateStepsMutation = useBulkCreateReportSteps();
  const bulkUpdateStepsMutation = useBulkUpdateReportSteps();
  const deleteStepMutation = useDeleteReportStep();

  // Site selection (create-only — edit mode hides this field)
  const { data: sitesData } = useSites({ page_size: 100 });
  const [selectedSiteId, setSelectedSiteId] = useState<number>(0);

  // No in-drawer `Editing: [This site] [District template]` toggle: the
  // drawer is for editing THIS protocol AT THIS SITE; district-tier
  // per-protocol overrides are accessed via the page-level "Default
  // Recipients" tab, which a toggle here would only duplicate.
  //
  // The rubric perm gate lives entirely on `ProtocolsList` at the page
  // tier, so `useAuth` is not consumed here for it.
  const siteOptions = useMemo(
    () =>
      sitesData?.results?.map((s) => ({
        label: s.name,
        value: String(s.id),
      })) ?? [],
    [sitesData],
  );

  // The site this drawer actually writes to: the protocol's own school when
  // editing, the picked site when creating, null before one is picked.
  // Computed once so the sound-library gate and every other consumer agree
  // on the same value.
  const effectiveSchoolId = protocol?.school ?? (selectedSiteId || null);

  // The alert + drill tone pickers below are gated
  // on SOUND_LIBRARY.
  //
  // `useScopedFeature`, NOT `useFeature`. SMS's `useFeature` unions across
  // every visible site when no active organization is selected (it answers
  // "may this user see the menu item at all?"), so an admin managing two
  // districts would get the pickers on a protocol belonging to the district
  // that did NOT buy the library — and its writes would 400 on the backend
  // gate. This surface authors ONE protocol at ONE site, so it resolves
  // against that site and fails closed: an unpicked site, an unresolvable
  // one, and a not-yet-loaded payload all read as OFF.
  const soundLibraryEnabled = useScopedFeature(
    FeatureCode.SOUND_LIBRARY,
    'site',
    effectiveSchoolId,
  );

  // Basic fields
  const [name, setName] = useState('');
  const [urgency, setUrgency] = useState<'emergency' | 'attention' | 'follow'>(
    'emergency',
  );
  const [activationMode, setActivationMode] = useState<'alert' | 'incident'>(
    'alert',
  );
  const [promptLocation, setPromptLocation] = useState(false);

  // ADR-144 Phase 1: Announce text and template
  const [announceText, setAnnounceText] = useState('');
  const [announceTemplate, setAnnounceTemplate] = useState('');
  const announceTemplateRef = useRef<HTMLTextAreaElement>(null);

  // Options
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isReverse, setIsReverse] = useState(false);
  const [isMissingDefault, setIsMissingDefault] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [reverseRadius, setReverseRadius] = useState('');
  const [reverseUnit, setReverseUnit] = useState('km');

  // Icon
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  // Sound
  const [soundFile, setSoundFile] = useState<File | null>(null);
  const [soundUrl, setSoundUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mp3InputRef = useRef<HTMLInputElement>(null);

  // Alert + drill sound. Held separately from
  // `soundFile`/`soundUrl` on purpose: these are scalar enums saved on the
  // alert-type body, NOT file uploads, and neither must ever disturb
  // `mp3_file` / `ogg_file`.
  const [alertSound, setAlertSound] = useState<AlertSound>(ALERT_SOUND_DEFAULT);
  const [drillSound, setDrillSound] = useState<DrillSound>(DRILL_SOUND_DEFAULT);

  // ONE audition state across BOTH pickers, holding the tone currently
  // playing or null. Not one per picker: the wire values are disjoint, so a
  // single slot is sufficient, and it makes "start an alert tone while a
  // drill tone is running" stop the drill instead of layering two tones over
  // each other. A second <audio> element could not offer that guarantee.
  const [previewingTone, setPreviewingTone] = useState<
    AlertSound | DrillSound | null
  >(null);
  const toneAudioRef = useRef<HTMLAudioElement>(null);

  // Groups
  const [selectedGroups, setSelectedGroups] = useState<GroupSelection[]>([]);
  const [showAll, setShowAll] = useState(true);
  const [elevateMembers, setElevateMembers] = useState(false);

  // Guidance
  const [guidances, setGuidances] = useState<GuidanceLocal[]>([]);

  // Report steps
  const [reportSteps, setReportSteps] = useState<ReportStepLocal[]>([]);

  // Load existing guidance and report steps for editing.
  //
  // The server's report-step list is phase-scoped and treats an absent `phase`
  // as `intake` (ADR 143 §3), so the survey questions need their own fetch —
  // one call cannot return both. The two calls are cached separately because
  // `phase` is part of the React Query key.
  const { data: existingGuidances } = useAlertGuidances(protocol?.id ?? 0);
  const { data: existingIntakeSteps } = useReportSteps(
    protocol?.id ?? 0,
    'intake',
  );
  const { data: existingSurveySteps } = useReportSteps(
    protocol?.id ?? 0,
    'end_event_survey',
  );

  // Reset form when drawer opens/protocol changes
  useEffect(() => {
    if (protocol) {
      setName(protocol.name);
      setUrgency(protocol.urgency);
      setActivationMode(protocol.activation_mode);
      setPromptLocation(protocol.prompt_location ?? false);
      setAnnounceText(protocol.announce_text ?? '');
      setAnnounceTemplate(protocol.announce_template ?? '');
      setRequiresApproval(protocol.requires_admin_approval ?? false);
      setIsDefault(protocol.is_default ?? false);
      setIsReverse(protocol.is_reverse ?? false);
      setIsMissingDefault(protocol.is_missing_default ?? false);
      setIsSystem(protocol.is_system ?? false);
      setReverseRadius(protocol.data?.radius?.value ?? '');
      setReverseUnit(protocol.data?.radius?.unit ?? 'km');
      setIconPreview(protocol.icon ?? null);
      setIconFile(null);
      setSoundUrl(protocol.mp3_file ?? null);
      setSoundFile(null);
      // BOTH tone fields are absent — not null — whenever
      // the org has not launched the sound library, because the backend's
      // SoundLibraryGateMixin pops them from the rendered payload. A protocol
      // saved before the tone fields existed is missing them too. Absent
      // reads as the column default in both cases, so the pickers are never
      // blank and a round trip through a gated org cannot rewrite a tone.
      setAlertSound(protocol.alert_sound ?? ALERT_SOUND_DEFAULT);
      setDrillSound(protocol.drill_sound ?? DRILL_SOUND_DEFAULT);
      setShowAll(
        !protocol.alert_to_specific_groups_only?.length ||
        (protocol.can_start_others_group_alert ?? false),
      );
      setElevateMembers(protocol.elevate_group_members ?? false);

      // Groups: we only have IDs, need names. Store IDs now, names resolve later.
      if (protocol.alert_to_specific_groups_only?.length) {
        setSelectedGroups(
          protocol.alert_to_specific_groups_only.map((id) => ({
            id,
            name: `Group #${id}`,
          })),
        );
      } else {
        setSelectedGroups([]);
      }
    } else {
      setSelectedSiteId(0);
      setName('');
      setUrgency('emergency');
      setActivationMode('alert');
      setPromptLocation(false);
      setAnnounceText('');
      setAnnounceTemplate('');
      setRequiresApproval(false);
      setIsDefault(false);
      setIsReverse(false);
      setIsMissingDefault(false);
      setIsSystem(false);
      setReverseRadius('');
      setReverseUnit('km');
      setIconPreview(null);
      setIconFile(null);
      setSoundUrl(null);
      setSoundFile(null);
      setAlertSound(ALERT_SOUND_DEFAULT);
      setDrillSound(DRILL_SOUND_DEFAULT);
      setSelectedGroups([]);
      setShowAll(true);
      setElevateMembers(false);
      setGuidances([]);
      setReportSteps([]);
    }
    setIsPlaying(false);
    setPreviewingTone(null);
  }, [protocol, isOpen]);

  // Sync existing guidance data
  useEffect(() => {
    if (existingGuidances?.results && isEditing) {
      setGuidances(
        existingGuidances.results.map((g) => ({
          id: g.id,
          title: g.title,
          plan: g.plan,
          is_default: g.is_default,
          steps: g.steps?.map((s) => ({
            id: s.id,
            row_type: s.row_type,
            title: s.title,
            description: s.description,
            order: s.order,
            is_tick_required: s.is_tick_required,
          })),
        })),
      );
    }
  }, [existingGuidances, isEditing]);

  // Sync existing report steps — both phases merged into one editable list.
  useEffect(() => {
    if (!isEditing) return;
    if (!existingIntakeSteps?.results && !existingSurveySteps?.results) return;

    const fromResponse = (
      results: ReportStep[] | undefined,
      fallbackPhase: ReportStepPhase,
    ): ReportStepLocal[] =>
      (results ?? []).map((s) => ({
        id: s.id,
        // Trust what the server stored. `fallbackPhase` only covers a response
        // predating the field, and it is the phase we asked for either way.
        phase: s.phase ?? fallbackPhase,
        field_type: s.field_type,
        label: s.label,
        field_slug: s.field_slug,
        placeholder: s.placeholder,
        description: s.description,
        options: s.options,
        order: s.order,
        is_required: s.is_required,
        allow_other: s.allow_other,
        parameter: s.parameter,
      }));

    setReportSteps([
      ...fromResponse(existingIntakeSteps?.results, 'intake'),
      ...fromResponse(existingSurveySteps?.results, 'end_event_survey'),
    ]);
  }, [existingIntakeSteps, existingSurveySteps, isEditing]);

  // ADR-068: Follow urgency forces incident mode
  useEffect(() => {
    if (urgency === 'follow') {
      setActivationMode('incident');
    }
  }, [urgency]);

  // ADR-068: Reverse alerts cannot use incident mode
  useEffect(() => {
    if (isReverse) {
      setActivationMode('alert');
    }
  }, [isReverse]);

  // Icon handlers
  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setIconPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleDefaultIcon = () => {
    setIconFile(null);
    setIconPreview(null);
  };

  // Sound handlers
  const handleSoundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSoundFile(file);
      setSoundUrl(URL.createObjectURL(file));
    }
  };

  const handleDefaultSound = () => {
    setSoundFile(null);
    setSoundUrl(null);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        // Ignore AbortError/NotAllowedError — benign during navigation or autoplay restriction
      });
    }
    setIsPlaying(!isPlaying);
  };

  // Tone audition. Same affordance as `togglePlay` above —
  // click to start, click again to stop — but keyed by tone, so clicking
  // Drill 2 while Drill 1 is playing switches rather than stacking, and so
  // does crossing between the alert and drill pickers. Selecting a tone and
  // auditioning a tone are deliberately separate actions: an admin can listen
  // to several before committing the choice.
  const toggleTonePreview = (value: AlertSound | DrillSound) => {
    setPreviewingTone((current) => (current === value ? null : value));
  };

  // The <audio> src is derived from `previewingTone`, so playback has to wait
  // for React to commit the new src — calling play() straight out of the click
  // handler would replay the PREVIOUS tone.
  useEffect(() => {
    const el = toneAudioRef.current;
    if (!el) return;
    if (previewingTone) {
      el.currentTime = 0;
      el.play?.()?.catch(() => {
        // Ignore AbortError/NotAllowedError — benign when the src swaps
        // mid-play or autoplay policy blocks an un-gestured play.
      });
    } else {
      el.pause?.();
    }
  }, [previewingTone]);

  // Save handler
  const handleSave = async () => {
    if (!isEditing && !selectedSiteId) {
      toaster.create({
        title: t('protocols.siteRequired', 'Please select a site'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!name.trim()) {
      toaster.create({
        title: t('protocols.nameRequired', 'Name is required'),
        type: 'error',
        duration: 3000,
      });
      return;
    }

    const alertTypePayload: Partial<AlertType> = {
      name: name.trim(),
      urgency,
      activation_mode: activationMode,
      announce_text: announceText.trim() || undefined,
      announce_template: announceTemplate.trim() || undefined,
      prompt_location: activationMode === 'incident' ? promptLocation : false,
      requires_admin_approval: requiresApproval,
      is_default: isDefault,
      is_reverse: isReverse,
      is_missing_default: isMissingDefault,
      is_system: isSystem,
      // Both tones ride the SCALAR body on create and
      // update. Neither may go anywhere near the `mp3_file` FormData below —
      // that request is a multipart PATCH whose only job is replacing the
      // real-alert audio, and folding a tone enum into it would couple the
      // two settings the feature exists to keep apart.
      //
      // Omitted ENTIRELY when the gate is off, rather than sent at their
      // defaults: a picker the admin cannot see puts nothing on the wire, so
      // an unlaunched org's payload carries no tone fields at all. The
      // backend gate tolerates a default-valued tone (it compares against the
      // column default rather than testing presence), but the client never
      // relies on that tolerance.
      ...(soundLibraryEnabled
        ? { alert_sound: alertSound, drill_sound: drillSound }
        : {}),
      can_start_others_group_alert: showAll,
      elevate_group_members: elevateMembers,
      alert_to_specific_groups_only: selectedGroups.map((g) => g.id),
      data:
        isReverse && reverseRadius
          ? { radius: { value: reverseRadius, unit: reverseUnit } }
          : undefined,
    };

    try {
      let alertTypeId: number;

      if (isEditing && protocol) {
        await updateMutation.mutateAsync({
          id: protocol.id,
          data: alertTypePayload,
        });
        alertTypeId = protocol.id;
      } else {
        const created = await createMutation.mutateAsync({
          ...alertTypePayload,
          school: selectedSiteId,
          sort_order: 0,
          is_active: true,
        });
        alertTypeId = created.id;
      }

      // Upload icon if changed
      if (iconFile) {
        const formData = new FormData();
        formData.append('icon', iconFile);
        await uploadMutation.mutateAsync({ id: alertTypeId, formData });
      }

      // Upload sound if changed
      if (soundFile) {
        const formData = new FormData();
        formData.append('mp3_file', soundFile);
        await uploadMutation.mutateAsync({ id: alertTypeId, formData });
      }

      // Save guidance changes
      await saveGuidances(alertTypeId);

      // Save report step changes
      await saveReportSteps(alertTypeId);

      toaster.create({
        title: isEditing
          ? t('protocols.protocolUpdated', 'Protocol updated')
          : t('protocols.protocolCreated', 'Protocol created'),
        type: 'success',
        duration: 3000,
      });
      onClose();
    } catch (err) {
      toaster.create({
        title: t('common.error', 'Error'),
        description:
          err instanceof Error
            ? err.message
            : t('common.unknownError', 'Something went wrong'),
        type: 'error',
        duration: 4000,
      });
    }
  };

  const saveGuidances = async (alertTypeId: number) => {
    for (const g of guidances) {
      if (g._isDeleted && g.id) {
        await deleteGuidanceMutation.mutateAsync(g.id);
      } else if (g._isNew && !g._isDeleted) {
        await createGuidanceMutation.mutateAsync({
          title: g.title,
          plan: g.plan,
          alert_type: alertTypeId,
          is_default: g.is_default ?? false,
          steps: g.steps
            ?.filter((s) => !s._isDeleted)
            .map((s) => ({
              row_type: s.row_type,
              title: s.title,
              description: s.description,
              order: s.order,
              is_tick_required: s.is_tick_required,
            })),
        });
      } else if (g.id && !g._isDeleted) {
        await updateGuidanceMutation.mutateAsync({
          id: g.id,
          data: {
            id: g.id,
            title: g.title,
            plan: g.plan,
            alert_type: alertTypeId,
            is_default: g.is_default ?? false,
            steps: g.steps
              ?.filter((s) => !s._isDeleted)
              .map((s) => ({
                id: s.id,
                row_type: s.row_type,
                title: s.title,
                description: s.description,
                order: s.order,
                is_tick_required: s.is_tick_required,
              })),
          },
        });
      }
    }
  };

  const saveReportSteps = async (alertTypeId: number) => {
    // Delete removed steps
    const toDelete = reportSteps.filter((s) => s._isDeleted && s.id);
    for (const s of toDelete) {
      await deleteStepMutation.mutateAsync(s.id!);
    }

    // Bulk create new steps.
    //
    // `phase` must be listed here explicitly: this is a whitelist, not a
    // spread, so anything omitted never reaches the server and silently takes
    // the model default (`intake`). A discriminator not persisted by every
    // writer would make every survey question an admin authored come back
    // as an intake one.
    const toCreate = reportSteps.filter((s) => s._isNew && !s._isDeleted);
    if (toCreate.length > 0) {
      await bulkCreateStepsMutation.mutateAsync(
        toCreate.map((s) => ({
          alert_type: alertTypeId,
          phase: s.phase,
          field_type: s.field_type,
          label: s.label,
          field_slug: s.field_slug,
          placeholder: s.placeholder,
          description: s.description,
          options: s.options,
          order: s.order,
          is_required: s.is_required,
          allow_other: s.allow_other,
          parameter: s.phase === 'intake' ? s.parameter : undefined,
        })),
      );
    }

    // Bulk update existing steps. `phase` is round-tripped rather than
    // reasserted from a form default, so editing a step's label cannot move it
    // between the intake form and the survey.
    const toUpdate = reportSteps.filter(
      (s) => s.id && !s._isNew && !s._isDeleted,
    );
    if (toUpdate.length > 0) {
      await bulkUpdateStepsMutation.mutateAsync(
        toUpdate.map((s) => ({
          id: s.id,
          alert_type: alertTypeId,
          phase: s.phase,
          field_type: s.field_type,
          label: s.label,
          field_slug: s.field_slug,
          placeholder: s.placeholder,
          description: s.description,
          options: s.options,
          order: s.order,
          is_required: s.is_required,
          allow_other: s.allow_other,
          parameter: s.phase === 'intake' ? s.parameter : undefined,
        })),
      );
    }
  };

  // `undefined`, not '', when nothing is auditioning — an empty `src`
  // attribute makes the browser re-request the current document as media.
  // Searches BOTH tables, since one element serves both pickers.
  const tonePreviewSrc =
    [...alertSoundOptions, ...drillSoundOptions].find(
      (opt) => opt.value === previewingTone,
    )?.previewSrc ?? undefined;

  // Static t() calls so check-client-locale-catalogs.mjs verifies these keys.
  const drillSoundLabel = (value: DrillSound) =>
    value === 'same_as_alert'
      ? t('protocols.drillSoundSameAsAlert', 'Same as alert')
      : value === 'drill_1'
        ? t('protocols.drillSoundDrill1', 'Drill 1')
        : value === 'drill_2'
          ? t('protocols.drillSoundDrill2', 'Drill 2')
          : t('protocols.drillSoundDrill3', 'Drill 3');

  /** The audition row under a picker. Shared by both cards so the two behave
   *  identically — same play/pause affordance, same stateful accessible name,
   *  same single-tone-at-a-time semantics. Every real tone in both
   *  vocabularies auditions; only the sentinels (`default`, `custom_upload`,
   *  `same_as_alert`) are absent, having no fixed asset of their own. */
  const tonePreviewRow = (
    previews: { value: AlertSound | DrillSound; label: string }[],
    e2ePrefix: string,
  ) => (
    <Flex gap={2} mt={3}>
      {previews.map(({ value, label }) => (
        <ButtonComponent
          key={value}
          visual="secondary"
          size="sm"
          onClick={() => toggleTonePreview(value)}
          data-e2e={`${e2ePrefix}-${value}`}
          // The accessible name is ONE translated sentence with the
          // tone interpolated, not an English verb concatenated onto a
          // translated label — that leaves screen-reader users on
          // de/es/fr/ja/zh hearing a half-translated control. Both keys are
          // STATIC literals, the only form check-client-locale-catalogs.mjs
          // can resolve, and the wording matches aegix-aim-react's
          // TonePickerSection so the two web apps say the same thing.
          aria-label={
            previewingTone === value
              ? t('protocols.tonePreviewStop', 'Stop previewing {{tone}}', {
                tone: label,
              })
              : t('protocols.tonePreview', 'Preview {{tone}}', { tone: label })
          }
        >
          <Flex as="span" alignItems="center" gap={2}>
            {previewingTone === value ? (
              <FaPause size={10} />
            ) : (
              <FaPlay size={10} />
            )}
            {label}
          </Flex>
        </ButtonComponent>
      ))}
    </Flex>
  );

  const alertSoundLabel = (value: AlertSound) =>
    value === 'default'
      ? t('protocols.alertSoundDefault', 'Default')
      : value === 'custom_upload'
        ? t('protocols.alertSoundCustomUpload', 'Custom upload')
        : value === 'alert_1'
          ? t('protocols.alertSoundAlert1', 'Alert 1')
          : value === 'alert_2'
            ? t('protocols.alertSoundAlert2', 'Alert 2')
            : t('protocols.alertSoundAlert3', 'Alert 3');

  const intakeParameterOptions = useMemo(() => {
    const intakeSteps = reportSteps.filter(
      (s) => !s._isDeleted && s.phase === 'intake',
    );
    return intakeSteps.map((s) => {
      const slug = s.field_slug || slugifyLabel(s.label);
      return {
        label: `${s.label} ({{intake.${slug}}})`,
        value: `{{intake.${slug}}}`,
      };
    });
  }, [reportSteps]);

  const handleInsertParameter = (token: string) => {
    const textarea = announceTemplateRef.current;
    if (
      textarea &&
      typeof textarea.selectionStart === 'number' &&
      typeof textarea.selectionEnd === 'number'
    ) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = announceTemplate.substring(0, start);
      const after = announceTemplate.substring(end);
      const updated = before + token + after;
      setAnnounceTemplate(updated);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    } else {
      setAnnounceTemplate((prev) => (prev ? `${prev} ${token}` : token));
    }
  };

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    uploadMutation.isPending;

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing
          ? `${t('protocols.editProtocol', 'Edit Protocol')} — ${protocol?.name}`
          : t('protocols.addProtocol', 'Add Protocol')
      }
      size="lg"
      e2eTitle="protocol-drawer"
      footer={
        <Flex direction="column" gap={2} width="100%">
          <SimpleGrid columns={2} gap={4} width="100%">
            <ButtonComponent
              visual="secondary"
              onClick={onClose}
              data-e2e="cancel-protocol-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              onClick={handleSave}
              disabled={isSaving}
              data-e2e="save-protocol-button"
            >
              {isSaving
                ? t('common.saving', 'Saving…')
                : isEditing
                  ? t('common.saveChanges', 'Save Changes')
                  : t('common.create', 'Create')}
            </ButtonComponent>
          </SimpleGrid>
          {isEditing && protocol?.id != null && (onArchive || onRestore) && (
            <Flex justifyContent="flex-end">
              {protocol.deleted
                ? onRestore && (
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    onClick={() => {
                      onRestore(protocol.id);
                      onClose();
                    }}
                    data-e2e="restore-protocol-button"
                  >
                    {t('protocols.restoreProtocol', 'Restore Protocol')}
                  </ButtonComponent>
                )
                : onArchive && (
                  <ButtonComponent
                    visual="secondary"
                    size="sm"
                    onClick={() => {
                      onArchive(protocol.id);
                      onClose();
                    }}
                    data-e2e="archive-protocol-button"
                  >
                    {t('protocols.archiveProtocol', 'Archive Protocol')}
                  </ButtonComponent>
                )}
            </Flex>
          )}
        </Flex>
      }
    >
      <Flex direction="column" gap={4}>
        {!isEditing && (
          <SelectInput
            label={t('nav.sites', 'Site')}
            placeholder={t('sites.selectSite', 'Select a site…')}
            value={selectedSiteId ? String(selectedSiteId) : ''}
            onChange={(v) => setSelectedSiteId(v ? Number(v) : 0)}
            option={siteOptions}
          />
        )}

        <Field.Root>
          <Field.Label htmlFor="protocol-name-input" srOnly>
            {t('protocols.protocolName', 'Protocol Name')}
          </Field.Label>
          <InputField
            id="protocol-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('protocols.protocolName', 'Protocol Name')}
            border="1px solid"
            borderColor="border.secondary"
            borderRadius="8px"
            fontSize="24px"
            textAlign="center"
            color="text.primary"
            fontWeight={600}
            data-e2e="protocol-name-input"
            py={3}
            _focusVisible={{ borderColor: 'border.primary', outline: 'none' }}
          />
        </Field.Root>

        <SectionCard title={t('protocols.media', 'Media')}>
          <Flex gap={6}>
            {/* Icon */}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
                {t('protocols.icon', 'Icon')}
              </Text>
              <Center
                w="100%"
                minH="80px"
                bg="surface.secondary"
                borderRadius="md"
                mb={2}
              >
                {iconPreview ? (
                  <Image
                    src={iconPreview}
                    maxH="64px"
                    maxW="64px"
                    objectFit="contain"
                  />
                ) : (
                  <MdNotificationsActive size={32} color="gray" />
                )}
              </Center>
              <Flex gap={2}>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={() => iconInputRef.current?.click()}
                  data-e2e="change-icon-button"
                >
                  {iconPreview
                    ? t('common.change', 'Change')
                    : t('protocols.addIcon', 'Add Icon')}
                </ButtonComponent>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={handleDefaultIcon}
                  data-e2e="default-icon-button"
                >
                  {t('common.default', 'Default')}
                </ButtonComponent>
              </Flex>
              <InputField
                ref={iconInputRef}
                type="file"
                accept="image/*"
                title={t('protocols.uploadIcon', 'Upload icon')}
                aria-label={t('protocols.uploadIcon', 'Upload icon')}
                hidden
                onChange={handleIconChange}
              />
            </Box>

            {/* Sound */}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
                {t('protocols.sound', 'Sound')}
              </Text>
              <Flex
                w="100%"
                minH="80px"
                bg="surface.secondary"
                borderRadius="md"
                mb={2}
                alignItems="center"
                justifyContent="center"
                gap={3}
                px={3}
              >
                {soundUrl || soundFile ? (
                  <>
                    <Box
                      as="button"
                      p={2}
                      borderRadius="full"
                      bg="brand.500"
                      color="white"
                      onClick={togglePlay}
                      data-e2e="toggle-sound-button"
                    >
                      {isPlaying ? <FaPause size={12} /> : <FaPlay size={12} />}
                    </Box>
                    <Text fontSize="xs" color="text.secondary" lineClamp={2}>
                      {soundFile?.name ??
                        t('protocols.customSound', 'Custom sound')}
                    </Text>
                  </>
                ) : (
                  <Text fontSize="sm" color="text.secondary">
                    {t('protocols.defaultSound', 'Default sound')}
                  </Text>
                )}
              </Flex>
              <Flex gap={2}>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={() => mp3InputRef.current?.click()}
                  data-e2e="change-sound-button"
                >
                  {soundUrl || soundFile
                    ? t('common.change', 'Change')
                    : t('protocols.addSound', 'Add Sound')}
                </ButtonComponent>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={handleDefaultSound}
                  data-e2e="default-sound-button"
                >
                  {t('common.default', 'Default')}
                </ButtonComponent>
              </Flex>
              <InputField
                ref={mp3InputRef}
                type="file"
                accept=".mp3,audio/mpeg"
                title={t('protocols.uploadSound', 'Upload sound')}
                aria-label={t('protocols.uploadSound', 'Upload sound')}
                hidden
                onChange={handleSoundChange}
              />
              {soundUrl && (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- alert-sound preview: short audio signal, no spoken content to caption
                <audio
                  ref={audioRef}
                  src={soundUrl}
                  onEnded={() => setIsPlaying(false)}
                  hidden
                />
              )}
            </Box>
          </Flex>
        </SectionCard>

        {/* The tone pickers. Their own SectionCards rather than extra
            columns inside "Media": the two controls above write FILES
            (icon, mp3_file) and these write scalar enums, and the whole
            point of the feature is that picking a tone leaves the uploaded
            audio alone. Sharing the Media card would invite exactly the
            mental model the feature exists to break.

            Both are gated as ONE unit — an org either has the sound library
            or it does not, and showing half of it would be worse than showing
            none. Rendering nothing (rather than a disabled control) is
            deliberate: `FeatureGate` is route-level and redirects, which is
            wrong for a section inside a drawer, so the gate is expressed as a
            plain conditional here. Nothing about the surrounding form
            changes when it is off. */}
        {soundLibraryEnabled && (
          <>
            <SectionCard
              title={t('protocols.alertSound', 'Alert Sound')}
              e2eTitle="alert-sound-section"
            >
              <Text fontSize="xs" color="text.secondary" mb={2}>
                {t(
                  'protocols.alertSoundHelp',
                  'Plays when this protocol is activated as a real alert. Default keeps the existing behaviour: the uploaded sound above when there is one, otherwise the app’s built-in tone.',
                )}
              </Text>
              <ToggleGroup
                options={alertSoundOptions.map<ToggleOption>((opt) => ({
                  value: opt.value,
                  label: alertSoundLabel(opt.value),
                  'data-e2e': `alert-sound-option-${opt.value}`,
                }))}
                value={alertSound}
                onChange={(v) => setAlertSound(v as AlertSound)}
                fillWidth
                aria-label={t('protocols.alertSound', 'Alert Sound')}
                data-e2e="alert-sound-toggle-group"
              />
              {tonePreviewRow(
                alertSoundOptions
                  .filter((opt) => opt.previewSrc)
                  .map((opt) => ({
                    value: opt.value,
                    label: alertSoundLabel(opt.value),
                  })),
                'toggle-alert-sound-button',
              )}
            </SectionCard>

            <SectionCard
              title={t('protocols.drillSound', 'Drill Sound')}
              e2eTitle="drill-sound-section"
            >
              <Text fontSize="xs" color="text.secondary" mb={2}>
                {t(
                  'protocols.drillSoundHelp',
                  'Plays when this protocol is activated as a drill. The alert sound above is used for real alerts and is not affected.',
                )}
              </Text>
              <ToggleGroup
                options={drillSoundOptions.map<ToggleOption>((opt) => ({
                  value: opt.value,
                  label: drillSoundLabel(opt.value),
                  'data-e2e': `drill-sound-option-${opt.value}`,
                }))}
                value={drillSound}
                onChange={(v) => setDrillSound(v as DrillSound)}
                fillWidth
                aria-label={t('protocols.drillSound', 'Drill Sound')}
                data-e2e="drill-sound-toggle-group"
              />
              {tonePreviewRow(
                drillSoundOptions
                  .filter((opt) => opt.previewSrc)
                  .map((opt) => ({
                    value: opt.value,
                    label: drillSoundLabel(opt.value),
                  })),
                'toggle-drill-sound-button',
              )}
            </SectionCard>

            {/* ONE element for BOTH pickers, src swapped by `previewingTone`,
                so auditioning any tone stops whatever was already playing —
                including across the two cards. A second element would let an
                alert tone and a drill tone overlap. Always mounted so the
                pause path in the effect always has a node to act on. */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- tone preview: short audio signal, no spoken content to caption */}
            <audio
              ref={toneAudioRef}
              src={tonePreviewSrc}
              onEnded={() => setPreviewingTone(null)}
              data-e2e="tone-preview-audio"
              hidden
            />
          </>
        )}

        <SectionCard
          title={t('protocols.announceMessaging', 'Announce & Messaging')}
          e2eTitle="announce-messaging-section"
        >
          <Flex direction="column" gap={5}>
            <Field.Root>
              <Field.Label htmlFor="protocol-announce-text-input" mb={1.5}>
                {t('protocols.announceText', 'Announce Text (Fallback)')}
              </Field.Label>
              <InputField
                id="protocol-announce-text-input"
                value={announceText}
                onChange={(e) => setAnnounceText(e.target.value)}
                placeholder={t(
                  'protocols.announceTextPlaceholder',
                  'e.g. Lockdown! Locks, Lights, Out of Sight.',
                )}
                data-e2e="protocol-announce-text-input"
              />
              <Text fontSize="xs" color="text.secondary" mt={1.5}>
                {t(
                  'protocols.announceTextHelp',
                  'Plain text broadcast sent to legacy devices and push notifications when no template parameters resolve.',
                )}
              </Text>
            </Field.Root>

            <Field.Root>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                gap={4}
              >
                <Field.Label
                  htmlFor="protocol-announce-template-input"
                  mb={0}
                  whiteSpace="nowrap"
                >
                  {t('protocols.announceTemplate', 'Announce Template')}
                </Field.Label>
                {intakeParameterOptions.length > 0 && (
                  <Box w="210px" flexShrink={0}>
                    <SelectInput
                      placeholder={t(
                        'protocols.insertParameter',
                        'Insert Parameter…',
                      )}
                      value=""
                      onChange={(val) => {
                        if (!val) return;
                        handleInsertParameter(val);
                      }}
                      option={intakeParameterOptions}
                      size="sm"
                      data-e2e="insert-parameter-select"
                    />
                  </Box>
                )}
              </Flex>
              <TextareaField
                id="protocol-announce-template-input"
                ref={announceTemplateRef}
                value={announceTemplate}
                onChange={(e) => setAnnounceTemplate(e.target.value)}
                placeholder={t(
                  'protocols.announceTemplatePlaceholder',
                  'e.g. Shelter - Intake Hazard',
                )}
                minH="75px"
                data-e2e="protocol-announce-template-input"
              />
              <Text fontSize="xs" color="text.secondary" mt={1.5}>
                {t(
                  'protocols.announceTemplateHelp',
                  'Use the dropdown above to insert dynamic responses from the intake form into the broadcast message.',
                )}
              </Text>
            </Field.Root>
          </Flex>
        </SectionCard>

        <SectionCard title={t('protocols.urgency', 'Urgency')}>
          <ToggleGroup
            options={urgencyOptions.map<ToggleOption>((opt) => ({
              value: opt,
              label:
                opt === 'emergency'
                  ? t('protocols.emergency', 'Emergency')
                  : opt === 'attention'
                    ? t('protocols.attention', 'Attention')
                    : t('protocols.follow', 'Follow'),
              'data-e2e': `urgency-option-${opt}`,
            }))}
            value={urgency}
            onChange={(v) => setUrgency(v as typeof urgency)}
            fillWidth
            // Names the radiogroup off the enclosing SectionCard's
            // own already-translated title, so no new catalog key.
            aria-label={t('protocols.urgency', 'Urgency')}
            data-e2e="urgency-toggle-group"
          />
        </SectionCard>

        <SectionCard title={t('protocols.activate', 'Activate')}>
          {urgency === 'follow' && (
            <Text fontSize="xs" color="text.secondary" mb={1}>
              {t(
                'protocols.followRequiresIncident',
                'Follow urgency requires Incident mode.',
              )}
            </Text>
          )}
          {isReverse && (
            <Text fontSize="xs" color="text.secondary" mb={1}>
              {t(
                'protocols.reverseCannotIncident',
                'Reverse alerts cannot use Incident mode.',
              )}
            </Text>
          )}
          <Box mb={2}>
            <ToggleGroup
              options={modeOptions.map<ToggleOption>((opt) => ({
                value: opt,
                label:
                  opt === 'alert'
                    ? t('events.toggle.alerts', 'Alert')
                    : t('protocols.incident', 'Incident'),
                disabled:
                  (opt === 'alert' && urgency === 'follow') ||
                  (opt === 'incident' && isReverse),
                'data-e2e': `activation-mode-option-${opt}`,
              }))}
              value={activationMode}
              onChange={(v) => setActivationMode(v as typeof activationMode)}
              fillWidth
              aria-label={t('protocols.activate', 'Activate')}
              data-e2e="activation-mode-toggle-group"
            />
          </Box>
          {activationMode === 'incident' && (
            <ToggleButton
              title={t('protocols.promptForLocation', 'Prompt for Location')}
              checked={promptLocation}
              onCheckedChange={() => setPromptLocation(!promptLocation)}
              e2eTitle="prompt-location-toggle"
            />
          )}
        </SectionCard>

        <SectionCard title={t('common.options', 'Options')}>
          <Flex direction="column" gap={3}>
            <ToggleButton
              title={t('protocols.approvalRequired', 'Approval Required')}
              checked={requiresApproval}
              onCheckedChange={() => setRequiresApproval(!requiresApproval)}
              e2eTitle="requires-approval-toggle"
            />
            <ToggleButton
              title={t('protocols.defaultAlert', 'Default Alert')}
              checked={isDefault}
              onCheckedChange={() => setIsDefault(!isDefault)}
              e2eTitle="default-alert-toggle"
            />
            <ToggleButton
              title={t('protocols.reverseAlert', 'Reverse Alert')}
              checked={isReverse}
              onCheckedChange={() => setIsReverse(!isReverse)}
              e2eTitle="reverse-alert-toggle"
            />
            {isReverse && (
              <Flex gap={2} ml={6} alignItems="center">
                <Field.Root>
                  <Field.Label htmlFor="reverse-radius-input" srOnly>
                    {t('protocols.reverseRadius', 'Reverse Radius')}
                  </Field.Label>
                  <InputField
                    id="reverse-radius-input"
                    value={reverseRadius}
                    onChange={(e) => setReverseRadius(e.target.value)}
                    placeholder={t('protocols.radiusPlaceholder', 'Radius')}
                    size="sm"
                    w="100px"
                    type="number"
                    data-e2e="reverse-radius-input"
                  />
                </Field.Root>
                <ToggleGroup
                  options={[
                    {
                      value: 'km',
                      label: t('protocols.km', 'km'),
                      'data-e2e': 'reverse-unit-km',
                    },
                    {
                      value: 'Miles',
                      label: t('protocols.miles', 'Miles'),
                      'data-e2e': 'reverse-unit-Miles',
                    },
                  ]}
                  value={reverseUnit}
                  onChange={(v) => setReverseUnit(v)}
                  aria-label={t('protocols.reverseRadius', 'Reverse Radius')}
                  data-e2e="reverse-unit-toggle-group"
                />
              </Flex>
            )}
            <ToggleButton
              title={t('protocols.defaultIncident', 'Default Incident')}
              checked={isMissingDefault}
              onCheckedChange={() => setIsMissingDefault(!isMissingDefault)}
              e2eTitle="default-incident-toggle"
            />
            <ToggleButton
              title={t(
                'protocols.systemProtocol',
                'System protocol (hidden from user pickers)',
              )}
              checked={isSystem}
              onCheckedChange={() => setIsSystem(!isSystem)}
              e2eTitle="system-protocol-toggle"
            />
          </Flex>
        </SectionCard>

        <SectionCard title={t('protocols.notify', 'Notify')}>
          <ProtocolGroupSelector
            schoolId={effectiveSchoolId}
            selectedGroups={selectedGroups}
            onGroupsChange={setSelectedGroups}
            showAll={showAll}
            onShowAllChange={setShowAll}
            elevateMembers={elevateMembers}
            onElevateMembersChange={setElevateMembers}
          />
          {activationMode === 'incident' && selectedGroups.length === 0 && (
            <Text fontSize="xs" color="text.danger" mt={1}>
              {t(
                'protocols.groupsRequiredForIncident',
                'Groups are required for Incident mode.',
              )}
            </Text>
          )}
        </SectionCard>

        <SectionCard title={t('protocols.recipients', 'Recipients')}>
          {(() => {
            // Drawer always renders Recipients in school scope.
            // The district-template variant is exclusively edited via the
            // page-level "Default Recipients" tab. null scope = create-flow /
            // no site picked yet — editor renders its own "save first" hint.
            const site = sitesData?.results?.find(
              (s) => s.id === effectiveSchoolId,
            );
            const effectiveDistrictId = site?.district ?? null;

            const scope: RubricScope | null =
              effectiveSchoolId != null
                ? {
                  tier: 'school',
                  schoolId: effectiveSchoolId,
                  // districtId rides in school scope so the editor can
                  // peek at the T1 parent for inheritance hints on rows
                  // without a T2 override.
                  districtId: effectiveDistrictId,
                }
                : null;

            return (
              <ProtocolRecipientsEditor
                scope={scope}
                alertTypeId={protocol?.id ?? null}
                urgency={urgency}
                mode={activationMode}
              />
            );
          })()}
        </SectionCard>

        <ProtocolContactsEditor alertTypeId={protocol?.id ?? null} />

        <SectionCard title={t('protocols.intakeForm', 'Intake Form')}>
          <ProtocolReportStepEditor
            steps={reportSteps}
            onStepsChange={setReportSteps}
          />
        </SectionCard>

        <SectionCard title={t('protocols.guidance', 'Guidance')}>
          <ProtocolGuidanceEditor
            guidances={guidances}
            onGuidancesChange={setGuidances}
          />
        </SectionCard>
      </Flex>
    </DrawerComponent>
  );
}
